import { DepthBufferReader, FullScreenPass } from "@faro-lotv/lotv";
import {
  Camera,
  Color,
  DataTexture,
  FloatType,
  Material,
  Matrix4,
  NearestFilter,
  NoBlending,
  RedFormat,
  ShaderMaterial,
  UnsignedByteType,
  Vector3,
  Vector4,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import { DepthSnapshot } from "./depth-surface";
import { SurfaceDepthCapture } from "./surface-depth-capture";
import {
  CYLINDER_DEVIATION_COLORS,
  type CylinderSurface,
} from "./cylinder-surface";

export type SurfaceSelection = DepthSnapshot & {
  cylinder?: CylinderSurface;
  cylinderSamples?: Float64Array;
  endCaps?: Float32Array;
  centerline?: Float32Array;
  rings?: Float32Array;
  id?: string;
  worldSpace?: boolean;
  coverage?: "growing" | "complete" | "partial";
  mask: Uint8Array;
  plane: Vector4;
  tolerance: number;
  viewProjection: Matrix4;
  origin: Vector3;
  positions: Float32Array;
  outline: Float32Array;
  cloudId: string;
  cameraId: string;
};

const vertexShader = `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const MAX_CAPTURE_SIZE = 384;
const MAX_CYLINDER_CAPTURE_SIZE = 1024;

const selectionShader = `
uniform sampler2D uMask;
uniform sampler2D uCapturedDepth;
uniform mat4 uCapture;
uniform mat4 uCaptureInverse;
uniform vec4 uPlane;
uniform float uTolerance;
uniform bool uSelected;
uniform bool uWorldSpace;
uniform float uCylinderRadius;
bool belongsToSurface(vec4 world) {
  if (uSelected) {
    vec4 captured = uCapture * world;
    vec3 ndc = captured.xyz / captured.w;
    float distanceToSurface = abs(dot(uPlane, world));
    if (uCylinderRadius > 0.0) {
      distanceToSurface = abs(length(ndc.xy) - 1.0) * uCylinderRadius;
      ndc = vec3(fract(atan(ndc.y, ndc.x) / 6.28318530718 + 0.5) * 2.0 - 1.0, ndc.z, 0.0);
    }
    vec2 uv = ndc.xy * 0.5 + 0.5;
    if (captured.w > 0.0 && all(lessThanEqual(abs(ndc), vec3(1.0))) && texture2D(uMask, uv).r > 0.5) {
      bool sameSurface = uWorldSpace;
      if (!uWorldSpace) {
        float oldDepth = texture2D(uCapturedDepth, uv).r;
        vec4 oldWorld = uCaptureInverse * vec4(ndc.xy, oldDepth * 2.0 - 1.0, 1.0);
        oldWorld /= oldWorld.w;
        sameSurface = distance(oldWorld.xyz, world.xyz) <= uTolerance * 3.0;
      }
      if (distanceToSurface <= uTolerance && sameSurface) {
        return true;
      }
    }
  }
  return false;
}
`;

const fragmentShader = `
precision highp float;
uniform sampler2D uColor;
uniform sampler2D uDepth;
uniform mat4 uInverse;
uniform vec3 uDeviationInside;
uniform vec3 uDeviationFitted;
uniform vec3 uDeviationOutside;
${selectionShader}
varying vec2 vUv;
void main() {
  float depth = texture2D(uDepth, vUv).r;
  vec4 color = texture2D(uColor, vUv);
  gl_FragDepth = depth;
  if (uSelected && depth < 1.0 && color.a > 0.0) {
    vec4 world = uInverse * vec4(vUv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
    world /= world.w;
    if (belongsToSurface(world)) {
      if (uCylinderRadius > 0.0) {
        vec4 local = uCapture * world;
        float deviation = (length(local.xy / local.w) - 1.0) * uCylinderRadius;
        float normalized = clamp(deviation / max(uTolerance, 0.000001), -1.0, 1.0);
        vec3 mapped = normalized < 0.0
          ? mix(uDeviationInside, uDeviationFitted, normalized + 1.0)
          : mix(uDeviationFitted, uDeviationOutside, normalized);
        color.rgb = mix(color.rgb, mapped, 0.9);
      } else {
        color.rgb = mix(color.rgb, vec3(1.0, 1.0, 0.0), 0.06);
      }
    }
  }
  gl_FragColor = color;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** Click-only depth readback plus world-anchored selection coloring. */
export class SurfaceSelectionPass extends FullScreenPass<ShaderMaterial> {
  captureShape: "plane" | "cylinder" = "plane";
  capturePoint: Vector3 | undefined;
  capture: ((snapshot: DepthSnapshot) => void) | undefined;
  private reader: DepthBufferReader | undefined;
  private localCapture: SurfaceDepthCapture | undefined;
  private mask: DataTexture | undefined;
  private depth: DataTexture | undefined;

  /** @param camera Camera rendering the point-cloud subscene. */
  constructor(public camera: Camera) {
    super(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        blending: NoBlending,
        uniforms: {
          uColor: { value: null },
          uDepth: { value: null },
          uMask: { value: null },
          uCapturedDepth: { value: null },
          uInverse: { value: new Matrix4() },
          uCapture: { value: new Matrix4() },
          uCaptureInverse: { value: new Matrix4() },
          uPlane: { value: new Vector4() },
          uTolerance: { value: 0 },
          uSelected: { value: false },
          uWorldSpace: { value: false },
          uCylinderRadius: { value: 0 },
          uDeviationInside: {
            value: new Color(CYLINDER_DEVIATION_COLORS.inside),
          },
          uDeviationFitted: {
            value: new Color(CYLINDER_DEVIATION_COLORS.fitted),
          },
          uDeviationOutside: {
            value: new Color(CYLINDER_DEVIATION_COLORS.outside),
          },
        },
      }),
    );
    this.enabled = false;
  }

  /** @param selection Replacement patch, or undefined to clear. */
  setSelection(selection: SurfaceSelection | undefined): void {
    this.mask?.dispose();
    this.depth?.dispose();
    this.mask = undefined;
    this.depth = undefined;
    const { uniforms } = this.material;
    uniforms.uSelected.value = !!selection;
    uniforms.uMask.value = null;
    uniforms.uCapturedDepth.value = null;
    if (!selection) return;
    this.mask = new DataTexture(
      selection.mask,
      selection.width,
      selection.height,
      RedFormat,
      UnsignedByteType,
    );
    this.depth = new DataTexture(
      selection.worldSpace ? new Float32Array([0.5]) : selection.depth,
      selection.worldSpace ? 1 : selection.width,
      selection.worldSpace ? 1 : selection.height,
      RedFormat,
      FloatType,
    );
    for (const texture of [this.mask, this.depth]) {
      texture.minFilter = NearestFilter;
      texture.magFilter = NearestFilter;
      texture.needsUpdate = true;
    }
    uniforms.uMask.value = this.mask;
    uniforms.uWorldSpace.value = !!selection.worldSpace;
    uniforms.uCylinderRadius.value = selection.cylinder?.radius ?? 0;
    uniforms.uCapturedDepth.value = this.depth;
    uniforms.uCapture.value.copy(selection.viewProjection);
    uniforms.uCaptureInverse.value.copy(selection.inverseViewProjection);
    uniforms.uPlane.value.copy(selection.plane);
    uniforms.uTolerance.value = selection.tolerance;
  }

  /**
   * @param material Point material to filter before writing depth.
   * @returns Cleanup restoring the original material hooks.
   */
  isolateMaterial(material: Material): () => void {
    const previousCompile = material.onBeforeCompile;
    const previousRender = material.onBeforeRender;
    const previousKey = material.customProgramCacheKey;
    const isolated = { value: false };
    const { material: selectionMaterial, camera: selectionCamera } = this;
    material.onBeforeCompile = function compileSurfaceIsolation(
      shader,
      renderer,
    ): void {
      previousCompile.call(this, shader, renderer);
      shader.uniforms = {
        ...shader.uniforms,
        ...selectionMaterial.uniforms,
        uSurfaceIsolated: isolated,
      };
      shader.vertexShader =
        `uniform bool uSurfaceIsolated;\n${selectionShader}\n${shader.vertexShader}`.replace(
          "void main() {",
          `void main() {
          if (uSurfaceIsolated && !belongsToSurface(modelMatrix * vec4(position, 1.0))) {
            gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
            gl_PointSize = 0.0;
            return;
          }`,
        );
    };
    material.onBeforeRender = function renderSurfaceIsolation(
      renderer,
      scene,
      camera,
      geometry,
      object,
      group,
    ): void {
      previousRender.call(
        this,
        renderer,
        scene,
        camera,
        geometry,
        object,
        group,
      );
      isolated.value = camera === selectionCamera;
    };
    material.customProgramCacheKey = function surfaceIsolationKey(): string {
      return `${previousKey.call(this)}:surface-isolation-v2`;
    };
    material.needsUpdate = true;
    return () => {
      material.onBeforeCompile = previousCompile;
      material.onBeforeRender = previousRender;
      material.customProgramCacheKey = previousKey;
      material.needsUpdate = true;
    };
  }

  /**
   * Render coloring and fulfill at most one pending capture.
   *
   * @param renderer Active renderer.
   * @param writeBuffer Destination color/depth target.
   * @param readBuffer Source point-cloud target.
   */
  render(
    renderer: WebGLRenderer,
    writeBuffer: WebGLRenderTarget,
    readBuffer: WebGLRenderTarget,
  ): void {
    const inverse = new Matrix4().multiplyMatrices(
      this.camera.matrixWorld,
      this.camera.projectionMatrixInverse,
    );
    if (this.capture && readBuffer.depthTexture) {
      const callback = this.capture;
      this.capture = undefined;
      if (this.captureShape === "cylinder" && this.capturePoint) {
        this.localCapture ??= new SurfaceDepthCapture();
        const snapshot = this.localCapture.read(
          renderer,
          readBuffer,
          this.capturePoint,
          inverse,
        );
        queueMicrotask(() => callback(snapshot));
      } else {
        const scale = Math.min(
          1,
          (this.captureShape === "cylinder"
            ? MAX_CYLINDER_CAPTURE_SIZE
            : MAX_CAPTURE_SIZE) / Math.max(readBuffer.width, readBuffer.height),
        );
        const width = Math.max(1, Math.round(readBuffer.width * scale));
        const height = Math.max(1, Math.round(readBuffer.height * scale));
        this.reader ??= new DepthBufferReader();
        this.reader.read(readBuffer.depthTexture, width, height, renderer);
        const snapshot = {
          depth: this.reader.depthBuffer().slice(),
          width,
          height,
          inverseViewProjection: inverse.clone(),
        };
        queueMicrotask(() => callback(snapshot));
      }
    }
    const { uniforms } = this.material;
    uniforms.uColor.value = readBuffer.texture;
    uniforms.uDepth.value = readBuffer.depthTexture;
    uniforms.uInverse.value.copy(inverse);
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    renderer.clear();
    this.fsQuad.render(renderer);
  }

  /** Release capture and highlight resources. */
  override dispose(): void {
    this.capture = undefined;
    this.reader?.dispose();
    this.localCapture?.dispose();
    this.mask?.dispose();
    this.depth?.dispose();
    super.dispose();
  }
}
